import React, { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { authApi } from '../api/authApi';
import { setUpdateTokenFunction, setRefreshingTokenState } from '../api/axiosInstance';
import authService from '../services/authService';

interface User {
  id: string;
  email: string;
  name: string;
  token: string;
  role: string;
  exp?: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isRefreshingToken: boolean;
  login: (token: string, refreshToken: string) => void;
  logout: () => void;
  hasRole: (role: string) => boolean;
  updateToken: (token: string, refreshToken: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);

  const updateUserFromToken = (token: string) => {
    const decoded = jwtDecode<any>(token);
    const role = decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    const nameid = decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
    const name = decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'];
    const email = decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || decoded.sub;

    return {
      id: nameid,
      email: email,
      name: name,
      token,
      role: role || 'User'
    };
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setIsAuthenticated(false);
  };

  const attemptTokenRefresh = async (refreshToken: string): Promise<boolean> => {
    setIsRefreshingToken(true);
    try {
      const response = await authApi.refreshToken(refreshToken);
      if (response.success && response.accessToken && response.refreshToken) {
        updateToken(response.accessToken, response.refreshToken);
        return true;
      }
    } catch (error) {
      console.error('Failed to refresh token:', error);
    } finally {
      setIsRefreshingToken(false);
    }
    return false;
  };

  const updateToken = (token: string, refreshToken: string) => {
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
    
    const userData = updateUserFromToken(token);
    setUser(userData);
    setIsAuthenticated(true);
    // Не сбрасываем isRefreshingToken здесь, так как это делается в attemptTokenRefresh
  };

  // Устанавливаем функции в axiosInstance сразу при создании компонента
  setUpdateTokenFunction(updateToken);
  setRefreshingTokenState(setIsRefreshingToken);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');

      if (token) {
        try {
          const decoded = jwtDecode<any>(token);
          const currentTime = Date.now() / 1000;
          
          // Если токен еще действителен
          if (decoded.exp && decoded.exp > currentTime) {
            const userData = updateUserFromToken(token);
            setUser(userData);
            setIsAuthenticated(true);
          } 
          // Если токен истек, но есть refreshToken - пытаемся обновить
          else if (refreshToken) {
            console.log('Token expired, attempting refresh...');
            const refreshSuccess = await attemptTokenRefresh(refreshToken);
            if (!refreshSuccess) {
              console.log('Token refresh failed, logging out...');
              handleLogout();
            } else {
              console.log('Token refresh successful');
            }
          } 
          // Если токен истек и нет refreshToken
          else {
            console.log('Token expired and no refresh token, logging out...');
            handleLogout();
          }
        } catch (error) {
          console.error('Error decoding token:', error);
          // Если есть refreshToken, пытаемся обновить
          if (refreshToken) {
            console.log('Token decode error, attempting refresh...');
            const refreshSuccess = await attemptTokenRefresh(refreshToken);
            if (!refreshSuccess) {
              console.log('Token refresh failed after decode error, logging out...');
              handleLogout();
            } else {
              console.log('Token refresh successful after decode error');
            }
          } else {
            console.log('Token decode error and no refresh token, logging out...');
            handleLogout();
          }
        }
      } else {
        console.log('No token found in localStorage');
      }
      
      setIsLoading(false);
      // Уведомляем authService, что инициализация завершена
      authService.setAppInitialized();
    };

    initializeAuth();
  }, []);

  const login = (token: string, refreshToken: string) => {
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.removeItem('anonymousId');
    
    const userData = updateUserFromToken(token);
    setUser(userData);
    setIsAuthenticated(true);
    
    // Уведомляем все компоненты об успешной авторизации
    window.dispatchEvent(new CustomEvent('userAuthenticated', { 
      detail: { user: userData } 
    }));
  };

  const hasRole = (role: string): boolean => {
    return user?.role === role;
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, isRefreshingToken, login, logout: handleLogout, hasRole, updateToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 